import { BEIGE_300, BEIGE_400, GRAY_900 } from 'constants/styling/theme'
import { Box, Grid, Typography } from '@mui/material'
import { HomeOutlined, ShoppingCartOutlined } from '@mui/icons-material'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useGetPropertyDetail, usePatchPropertyDetail } from 'dataQueries'

import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { AddressMap } from 'components/common/AdressMap'
import { AutocompleteInput } from 'components/common/AdressMap/AutocompleteInput'
import { GoogleAPIPlace } from 'models/purchaseFlow'
import { Label } from 'components/common/Label'
import { LoadMapScriptWrapper } from 'components/common/AdressMap/LoadMapScriptWrapper'
import { MUIInputField } from 'components/common/MUIInputField'
import { Nullable } from 'models/helpers'
import { PropertyType } from 'models/property'
import { SaveFloatingToolBar } from 'components/common/SaveFloatingToolBar'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import Stack from '@mui/material/Stack'
import { ToggleButton } from 'components/common/ToggleButton'
import { getPropertyAddressFromGooglePlace } from 'utils/location/googlePlaceUtils'
import { isAddressValid } from 'utils/validators'
import moment from 'moment'
import { useClientProperty } from '../_main/contexts'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

export const ClientPropertyInformationContent: React.FC = () => {
  const prefilled = useRef<boolean>(false)
  const { i18n, t } = useTranslation(['property', 'common'])
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  const { baseUserData, userPropertyWorkspaceId, currentUserWorkspace } = useUserData()

  const {
    propertyId,
    propertyData,
    isPropertyAddressPrefilled,
  } = useClientProperty()

  const updatePropertyDetail = usePatchPropertyDetail()
  const getPropertyDetail = useGetPropertyDetail(propertyId, userPropertyWorkspaceId, false)

  const [propertyName, setPropertyName] = useState<string>(propertyData?.title || '')
  const [propertyDestination, setPropertyDestination] = useState<Nullable<PropertyType>>(propertyData?.propertyType)
  const [selectedPropertyPlace, setSelectedPropertyPlace] = useState<GoogleAPIPlace | null>(null)
  const [resetInitialPlace, setResetInitialPlace] = useState<boolean>(false)

  const { initialAddress, initialMarker } = useMemo(() => {
    if (isPropertyAddressPrefilled && !!propertyData?.address) {
      const { street, streetNumber, city, postalCode, country, coordinates } = propertyData?.address
      const address = `${street} ${streetNumber}, ${city}, ${postalCode}, ${country}`
      return {
        initialAddress: address,
        initialMarker: coordinates,
      }
    }

    return {
      initialAddress: undefined,
      initialMarker: undefined,
    }
  }, [isPropertyAddressPrefilled, propertyData?.address])

  const handleMapOnChange = useCallback((newGooglePlace: GoogleAPIPlace | null, isInitialLookup: boolean) => {
    if (!isInitialLookup) setSelectedPropertyPlace(newGooglePlace)
    if (isInitialLookup && isPropertyAddressPrefilled && !prefilled.current) {
      prefilled.current = true

      setSelectedPropertyPlace(newGooglePlace)
    }
  }, [isPropertyAddressPrefilled])

  const addressIsValid = useMemo(() => isAddressValid(selectedPropertyPlace), [selectedPropertyPlace])
  const isToolbarOpen = useMemo(() => {
    const hasNameChanged = Boolean(propertyName && propertyName !== propertyData?.title)
    const hasAddressChanged = Boolean(
      addressIsValid &&
      (
        selectedPropertyPlace?.geometry.location.lat !== initialMarker?.lat ||
        selectedPropertyPlace?.geometry.location.lng !== initialMarker?.lng
      )
    )
    const hasDestinationChanged = Boolean(
      (propertyDestination !== propertyData?.propertyType)
    )

    return hasNameChanged || hasAddressChanged || hasDestinationChanged
  }, [propertyName, propertyData?.title, propertyData?.propertyType, addressIsValid, selectedPropertyPlace?.geometry.location.lat, selectedPropertyPlace?.geometry.location.lng, initialMarker?.lat, initialMarker?.lng, propertyDestination])

  const payload = {
    title: propertyName,
    propertyType: propertyDestination,
    propertyUsage: propertyDestination,
    address: selectedPropertyPlace ? getPropertyAddressFromGooglePlace(selectedPropertyPlace) : undefined,
  }

  return (
    <LoadMapScriptWrapper
      language={i18n.language}
      countryCode={baseUserData?.country}
    >
      <Stack paddingY="3.2rem">
        <Grid container spacing={2.5} alignItems="start" direction={{ xs: 'column', sm: 'row' }}>
          <Grid item xs={12} md={4} width="100%" className="fs-exclude">
            <SectionedBorderBox
              title={
                <Typography variant='text-md' fontWeight={600} color={GRAY_900}>
                  {t('client.information_page.internal_information')}
                </Typography>
              }
            >
              <Stack gap={1.6}>

                <Stack gap={.4}>
                  <Label text={t('client.information_page.property_manager')} />

                  <Box
                    gap={.4}
                    sx={{ backgroundColor: BEIGE_300 }}
                    padding="1.2rem"
                    borderRadius=".8rem"
                    border={`1px solid ${BEIGE_400}`}
                  >

                    <Label text={propertyData?.broker?.name ?? ''} color={GRAY_900} />

                    <Stack direction={'row'} gap={.4}>

                      <Label text={t('client.information_page.broker_email')} />
                      <Typography variant='text-sm' fontWeight={500} color={GRAY_900}>
                        {propertyData?.broker?.email ?? t('common:not_applicable')}
                      </Typography>

                    </Stack>

                    <Stack direction={'row'} gap={.4}>

                      <Label text={t('client.information_page.broker_phone')} />
                      <Typography variant='text-sm' fontWeight={500} color={GRAY_900}>
                        {propertyData?.broker?.phone ?? t('common:not_applicable')}
                      </Typography>

                    </Stack>
                  </Box>

                </Stack>

                <Stack gap={.4}>

                  <Label text={t('client.information_page.creation_date')} />
                  <Label text={moment(propertyData?.createdOn).format('DD/MM/YYYY') ?? t('common:not_applicable')} color={GRAY_900} />

                </Stack>

              </Stack>
            </SectionedBorderBox>
          </Grid>

          <Grid item xs={12} md={8} width="100%" className="fs-mask">
            <SectionedBorderBox
              sx={{ width: '100%', paddingRight: '2rem' }}
              title={
                <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
                  {t('client.information_page.title')}
                </Typography>
              }
            >
              <Grid container spacing={2.5} alignItems="center" direction={{ xs: 'column', sm: 'row' }}>

                <Grid item xs={4}>
                  <Label text={t('client.information_page.property_name')} />
                </Grid>

                <Grid item xs={8}>
                  <MUIInputField
                    inputBoxWidth="28.6rem"
                    value={propertyName}
                    onChange={(e) => { setPropertyName(e.target.value) }}
                  />
                </Grid>

                <Grid item xs={4}>
                  <Label text={t('client.information_page.property_address')} />
                </Grid>
                <Grid item xs={8}>
                  <AutocompleteInput
                    handleChange={(newGooglePlace, isInitialLookup) => {
                      handleMapOnChange(newGooglePlace, isInitialLookup)
                    }}
                    onInputChange={e => setSelectedPropertyPlace(null)}
                    error={(selectedPropertyPlace && !addressIsValid) ? t('address_not_selected') : undefined}
                  />
                </Grid>

                {/* Map */}
                <Grid item xs={12}>
                  <AddressMap
                    height="40rem"
                    draggable={true}
                    language={i18n.language}
                    countryCode={baseUserData?.country}
                    initialAddress={initialAddress}
                    initialMarker={initialMarker}
                    isMapPinAdjusted={true}
                    handleChange={handleMapOnChange}
                    resetInitialPlace={resetInitialPlace}
                    setResetInitialPlace={setResetInitialPlace}
                  />
                </Grid>

                <Grid item xs={4}>
                  <Label text={t('client.information_page.property_destination')} />
                </Grid>
                <Grid item xs={8}>
                  <Stack gap={0.8} flexDirection="row">
                    {Object.values(PropertyType)
                      .sort((a, b) => (a === PropertyType.RESIDENTIAL ? -1 : b === PropertyType.RESIDENTIAL ? 1 : 0))
                      .map((type) => (
                        <ToggleButton
                          key={type}
                          isSelected={propertyDestination === type}
                          label={t(`client.information_page.destination.${type}`)}
                          sx={{ width: '100%', height: '4rem', justifyContent: 'center' }}
                          onClick={() => setPropertyDestination(type)}
                          typographyVariant='text-md'
                          icon={type === PropertyType.RESIDENTIAL
                            ? <HomeOutlined />
                            : <ShoppingCartOutlined />
                          }
                        />
                      ))}
                  </Stack>
                </Grid>
              </Grid>
            </SectionedBorderBox>
          </Grid>
        </Grid>

        <SaveFloatingToolBar
          isToolBarOpen={isToolbarOpen}
          onSave={() => {
            updatePropertyDetail.mutate(
              { propertyId, workspaceId: userPropertyWorkspaceId, payload },
              {
                onSuccess: () => {
                  logAnalyticsEvent(AnalyticsEvent.PROPERTY_INFORMATION_SAVED, {
                    propertyId,
                    realtyId: propertyId,
                    propertyName,
                    propertyType: propertyDestination,
                    planId: currentUserWorkspace?.id,
                  })
                  spawnSuccessToast(t('client.information_page.update_info_success_text'), { title: t('client.information_page.update_info_success_title'), timeout: 3000 })
                  getPropertyDetail.refetch()
                  if (getPropertyDetail.isSuccess) setResetInitialPlace(true)
                },
                onError: () => {
                  spawnErrorToast(t('client.information_page.update_info_error_text'), { title: t('client.information_page.update_info_error_title'), timeout: 3000 })
                }
              }
            )
          }}
          onDiscard={() => {
            setPropertyName(propertyData?.title || '')
            setResetInitialPlace(true)
            setPropertyDestination(propertyData?.propertyType)
          }}
          isLoadingSave={updatePropertyDetail.isPending}
          isLoadingDiscard={resetInitialPlace}
        />

      </Stack>
    </LoadMapScriptWrapper>
  )
}
